/** /org/personas/:email — la ficha de una persona; sus clientes solo si quien mira es ella, su líder o el roster. */
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Lock } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { EmptyState, LoaderBlock } from '@/components/internal/shared';
import { useOrgAccess, useOrgPerson } from '@/hooks/useOrg';
import { OrgAccessSection } from '@/components/org/OrgAccessSection';
import { OrgAssignmentsTable, OrgPersonCard } from '@/components/org/org-ui';

export function OrgPersonPage() {
  const { t } = useTranslation();
  const { email } = useParams<{ email: string }>();
  // react-router ya decodifica los params: nada de decodeURIComponent (un % suelto tiraba URIError).
  const q = useOrgPerson(email);
  const access = useOrgAccess();
  if (q.isLoading) return <LoaderBlock />;
  if (q.isError && q.error.data?.code !== 'NOT_FOUND') return <EmptyState title={t('org.home.error', 'No se pudo cargar el Org Service. Reintentá en unos segundos.')} />;
  if (q.isError || !q.data) return <EmptyState title={t('org.person.not_found', 'Esa persona no está en el Org Service (se sincroniza a diario desde el Community Map).')} />;
  const { person, solution, leader, assignments, assignmentsHidden } = q.data;
  return (
    <div className="space-y-4 pb-24">
      <Link to="/org?tab=people" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline"><ArrowLeft className="h-4 w-4" />{t('org.person.back', 'Personas')}</Link>
      <OrgPersonCard person={person} solutionSlug={solution?.slug ?? person.solution_slug} leader={leader} />
      <div>
        <h3 className="mb-2 text-sm font-semibold">{t('org.person.assignments', 'Clientes con los que trabaja')}</h3>
        {assignmentsHidden || !assignments ? (
          <Alert>
            <Lock className="h-4 w-4" />
            <AlertTitle>{t('org.person.hidden_title', 'Solo lo ven su líder y el roster')}</AlertTitle>
            <AlertDescription>{t('org.person.hidden_body', 'Con qué clientes trabaja cada persona lo ven ella misma, su línea de liderazgo en el Community Map y quienes están en los permisos del Org Service.')}</AlertDescription>
          </Alert>
        ) : (
          <OrgAssignmentsTable rows={assignments} hide={['person']} />
        )}
      </div>
      <div>
        <h3 className="mb-2 text-sm font-semibold">{t('org.person.access', 'Accesos que le corresponden')}</h3>
        <OrgAccessSection email={person.email} employeeId={person.employee_id} canRecord={!!access.data?.canAdmin} />
      </div>
    </div>
  );
}
